/**
 * Deliverable 16 — procurement, ordered by the generated order-by date.
 *
 * `order_by_date` is not typed by anyone. A.2.13 declares it
 * `GENERATED ALWAYS AS (needed_on_site - lead_time_days) STORED`, so the
 * database derives it from the date the material is needed and the supplier's
 * lead time, and this table only reports it back with the days remaining.
 *
 * A row whose order-by date has passed while the item is still unordered is
 * the §4.6 condition that raises the project's `schedule_at_risk` flag, so it
 * is marked here with the same weight the flag carries.
 */
import type { ProcurementItem } from '../lib/api';
import { shortDate } from '../lib/status';

interface Props {
  items: ProcurementItem[];
  atRisk: number;
  showProject?: boolean;
}

const STATUS_LABEL: Record<string, string> = {
  identified: 'Identified',
  quoted: 'Quoted',
  approved: 'Approved',
  ordered: 'Ordered',
  in_transit: 'In transit',
  delivered: 'Delivered',
  installed: 'Installed',
  cancelled: 'Cancelled',
};

export function ProcurementTable({ items, atRisk, showProject = true }: Props): JSX.Element {
  if (items.length === 0) {
    return (
      <div className="empty-state">
        <p className="text-body font-medium text-text-primary">No procurement items recorded</p>
        <p className="mt-1 text-body">
          Each item's order-by date is derived by the database from the date it is needed on site
          minus the supplier lead time. Nothing is listed until an item is entered.
        </p>
      </div>
    );
  }

  return (
    <>
      {atRisk > 0 ? (
        <p role="status" className="mb-2 flex items-center gap-2 text-body text-danger">
          <span aria-hidden="true" className="block h-2 w-2 rounded-full bg-danger" />
          <span className="numeric text-danger">{atRisk}</span> item
          {atRisk === 1 ? '' : 's'} past their order-by date and not yet ordered.
        </p>
      ) : null}

      {/* One horizontal scroll region rather than a squeezed table on a phone. */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] border-collapse text-body">
          <thead>
            <tr className="table-header text-left">
              <th scope="col" className="px-3 py-2 font-medium">
                Item
              </th>
              {showProject ? (
                <th scope="col" className="px-3 py-2 font-medium">
                  Project
                </th>
              ) : null}
              <th scope="col" className="px-3 py-2 font-medium">
                Needed on site
              </th>
              <th scope="col" className="px-3 py-2 font-medium">
                Lead time
              </th>
              <th scope="col" className="px-3 py-2 font-medium">
                Order by
              </th>
              <th scope="col" className="px-3 py-2 font-medium">
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr
                key={item.id}
                className={`table-row align-top ${item.orderByPassed ? 'procurement-row--late' : ''}`}
              >
                <td className="px-3 py-2">
                  <span className="font-medium text-text-primary">{item.description}</span>
                  <span className="block text-micro text-text-secondary">
                    <span className="numeric">{item.quantity}</span>
                    {item.unit ? ` ${item.unit}` : ''}
                    {item.supplierName ? ` · ${item.supplierName}` : ''}
                    {item.poNumber ? ` · PO ${item.poNumber}` : ''}
                  </span>
                </td>
                {showProject ? (
                  <td className="px-3 py-2 text-text-secondary">{item.projectCode}</td>
                ) : null}
                <td className="numeric px-3 py-2">
                  {item.neededOnSite ? shortDate(item.neededOnSite) : '\u2014'}
                </td>
                <td className="numeric px-3 py-2">{item.leadTimeDays} d</td>
                <td className="px-3 py-2">
                  {item.orderByDate ? (
                    <>
                      <span className="numeric">{shortDate(item.orderByDate)}</span>
                      <span
                        className={`block text-micro ${
                          item.orderByPassed ? 'text-danger' : 'text-text-secondary'
                        }`}
                      >
                        {daysText(item.daysUntilOrderBy)}
                      </span>
                    </>
                  ) : (
                    <span className="text-micro text-text-secondary">
                      No need-by date recorded
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <span
                    className={`inline-flex rounded-pill px-2 py-1 text-micro font-medium ${
                      item.status === 'ordered' ||
                      item.status === 'in_transit' ||
                      item.status === 'delivered' ||
                      item.status === 'installed'
                        ? 'status-pill--active'
                        : 'border border-border bg-white text-text-secondary'
                    }`}
                  >
                    {STATUS_LABEL[item.status] ?? item.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function daysText(days: number | null): string {
  if (days === null) return '';
  if (days === 0) return 'due today';
  if (days < 0) return `${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} overdue`;
  return `in ${days} day${days === 1 ? '' : 's'}`;
}
